import { useEffect, type FC } from 'react';
import { Space } from '@g2u/ui-kit';
import { useParams, useNavigate } from 'react-router';
import { observer } from 'mobx-react-lite';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Spin, Tabs } from 'antd';

import {
  Actions,
  CustomerDetails,
  CustomerJobs,
  CustomerNotes,
  CustomerDiscounts,
  CustomerTickets,
} from './components';
import { useCheckLock } from 'hooks/useCheckLock';
import { Toast, parseErrors } from 'helpers';
import { RecordLockType, type JsonDataItem } from 'types';
import { getCustomer, putCustomer } from 'queries';
import { RootStore } from 'store';
import { Content } from 'components';
import { customerFileTabs } from 'data';
import styles from './edit.module.scss';
import { CustomerHeadingRibbon } from 'components/common/customer-heading-ribbon';
import { MessageList } from 'components/common/message-list';

export const CustomerEditPage: FC = observer(() => {
  const { id, tab } = useParams();
  const navigate = useNavigate();
  const { setBreadcrumbs, setCustomerProfile, customerProfile } = RootStore;
  const { isLocked } = useCheckLock({ recordId: id ? Number(id) : undefined, recordLockType: RecordLockType.Customer });

  useEffect(() => {
    setBreadcrumbs([
      {
        url: '/customer/search',
        title: 'Find a customer',
      },
      {
        title: `Customer File ${id}`,
      },
    ]);

    return () => {
      setBreadcrumbs(null);
    };
  }, [id]);

  const { isInitialLoading: isCustomerLoading } = useQuery(['customer-data', id], getCustomer, {
    enabled: !!id,
    onSuccess: (data) => {
      setCustomerProfile(data);
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const {
    mutate: updateCustomer,
    isLoading: updatingCustomer,
    isSuccess: isCustomerUpdated,
  } = useMutation(putCustomer, {
    onSuccess: (data) => {
      setCustomerProfile(data);
      Toast.show({
        description: `New changes on customer file #${data.ID} have been saved.`,
        type: Toast.Type.Success,
      });
    },
    onError: (data: JsonDataItem) => {
      Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
    },
  });

  const handleChangeTab = (tabId: string) => {
    navigate(`/customer/edit/${id}/${tabId}`);
  };

  const mapTabs = (tabId: string) => {
    switch (tabId) {
      case 'details':
        return (
          <CustomerDetails
            customer={customerProfile}
            onUpdate={(payload) => {
              updateCustomer(payload);
            }}
            isUpdating={updatingCustomer}
            isUpdated={isCustomerUpdated}
            isLocked={isLocked}
          />
        );
      case 'jobs':
        return <CustomerJobs customerId={id} />;
      case 'discounts':
        return <CustomerDiscounts customerId={id} />;
      case 'notes':
        return <CustomerNotes customerId={id} />;
      case 'tickets':
        return <CustomerTickets customerId={id} />;
      case 'emails':
        return <MessageList for="customer" id={id || ''} messageType="emails" />;
      case 'sms':
        return <MessageList for="customer" id={id || ''} messageType="sms" />;
      default:
        return <>new form here</>;
    }
  };

  useEffect(() => {
    return () => setCustomerProfile(null);
  }, []);

  return (
    <Content dataTestId="create-customer-page" className={styles.edit}>
      <title>Edit Customer</title>

      <CustomerHeadingRibbon customer={customerProfile}>
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Large} className={styles.edit__container}>
          <Tabs
            activeKey={tab}
            onChange={handleChangeTab}
            defaultActiveKey={tab ?? 'details'}
            tabPosition="top"
            style={{ minHeight: 220 }}
            items={customerFileTabs.map((tab) => {
              return {
                label: tab.title,
                key: tab.id,
                children: isCustomerLoading ? (
                  <Space.$ justify={Space.Justify.Center}>
                    <Spin style={{ margin: '0 auto', padding: 40 }} />
                  </Space.$>
                ) : (
                  mapTabs(tab.id)
                ),
              };
            })}
          />
        </Space.$>
      </CustomerHeadingRibbon>
      <Actions customer={customerProfile} isDisabled={isLocked || updatingCustomer} />
    </Content>
  );
});
